import {
    Avatar,
    Badge,
    Box,
    Button,
    HStack,
    Stack,
    Text,
    VStack,
} from "@chakra-ui/react";
import { EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";

import ActionButton from "./ActionButton";

export type CardVariant = "received" | "sent" | "connection";

const COOLDOWN_DAYS = 7;

/** Returns remaining ms until cooldown expires, or 0 if already expired. */
const getRemainingMs = (updatedAt: string): number => {
    const updated = new Date(updatedAt).getTime();
    if (isNaN(updated)) return 0;
    const expiresAt = updated + COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    return Math.max(0, expiresAt - Date.now());
};

/** Formats remaining milliseconds into "Xd Xh Xm" string. */
const formatRemaining = (ms: number): string => {
    if (ms <= 0) return "";
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(" ");
};

/** Live countdown hook — ticks every minute, triggers re-render when cooldown expires. */
const useCooldownTimer = (updatedAt?: string): { inCooldown: boolean; label: string } => {
    const [remaining, setRemaining] = useState<number>(0);

    useEffect(() => {
        // Treat empty/falsy strings the same as undefined
        if (!updatedAt?.trim()) {
            setRemaining(0);
            return;
        }
        const tick = () => setRemaining(getRemainingMs(updatedAt));
        tick(); // compute immediately on mount / when updatedAt changes
        const id = setInterval(tick, 60_000);
        return () => clearInterval(id);
    }, [updatedAt]);

    return {
        inCooldown: remaining > 0,
        label: remaining > 0 ? `You can send interest again in ${formatRemaining(remaining)}.` : "",
    };
};

interface InterestProfileCardProps {
    variant: CardVariant;
    name: string;
    age: number;
    height: string;
    subtitle: string;
    location?: string;
    timeLabel: string;
    avatarUrl?: string;

    status?: string;
    /** ISO timestamp from backend — used to calculate DECLINED cooldown. */
    updatedAt?: string;

    // extra fields (backend flat DTO)
    dateOfBirth?: string;
    heightInCm?: number;
    designation?: string;
    employmentSector?: string;
    employerName?: string;

    phone?: string;
    email?: string;

    onAccept?: () => void;
    onDecline?: () => void;
    onCancel?: () => void;
    onViewContact?: () => void;
    /** Called when cooldown has expired and user wants to re-send interest. */
    onSendInterest?: () => void;
}

export const InterestProfileCard = ({
    variant,
    name,
    age,
    height,
    subtitle,
    location,
    timeLabel,
    avatarUrl,
    status,
    updatedAt,
    phone,
    email,
    employerName,
    onAccept,
    onDecline,
    onCancel,
    onViewContact,
    onSendInterest,
    }: InterestProfileCardProps) => {

    const normalizedStatus = String(status || "").trim().toUpperCase();
    const { inCooldown, label: cooldownLabel } = useCooldownTimer(
        normalizedStatus === "DECLINED" ? updatedAt : undefined
    );

    const statusColorScheme: "green" | "red" | "yellow" | "gray" =
      normalizedStatus === "ACCEPTED"
        ? "green"
        : normalizedStatus === "DECLINED"
          ? "red"
          : normalizedStatus === "PENDING" || normalizedStatus === "HOLD"
            ? "yellow"
            : "gray";

    return (
    <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        p={{ base: 4, md: 5 }}
        w="100%"
    >
        <Stack
            w="100%"
            direction={{ base: "column", md: "row" }}
            spacing={3}
            alignItems="center"
        >
            {/* LEFT: PROFILE */}
            <HStack w={{ base: "100%", md: "60%" }} align="flex-start" spacing={2}>
                <Avatar size="lg" src={avatarUrl} />

                <VStack w="100%" align="start" spacing={1} >
                    <Text color="#2b1b1a" fontWeight="semibold" fontSize="md" >
                    {name}
                    </Text>

                    <Text fontSize="sm" color="gray.600">
                    {timeLabel} {age} yrs • {height}
                    </Text>

                    <Text fontSize="sm" color="#2b1b1a">
                    {subtitle} • {employerName}
                    </Text>

                    {location && (
                    <Text color="#2b1b1a" fontSize="sm">
                        {location}
                    </Text>
                    )}
                </VStack>
            </HStack>

            {/* RIGHT: ACTIONS */}
            <VStack
                align={{ base: "stretch", md: "flex-end" }}
                spacing={3}
                flex={1}
                w={{ base: "100%", md: "40%" }}
            >
                {/* STATUS (Sent only) */}
                {variant === "sent" && normalizedStatus ? (
                    <Badge
                        alignSelf={{ base: "flex-start", md: "flex-end" }}
                        colorScheme={statusColorScheme}
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="xs"
                    >
                    {normalizedStatus}
                    </Badge>
                ) : null}

                {/* RECEIVED ACTIONS */}
                {variant === "received" && (
                    <Stack
                        direction={{ base: "column", sm: "row" }}
                        spacing={3}
                        w="100%"
                    >
                    <ActionButton
                        variant="primary"
                        onClick={onAccept ?? (() => {})}
                        label="Accept"
                    />
                    <ActionButton
                        variant="secondary"
                        onClick={onDecline ?? (() => {})}
                        label="Decline"
                    />
                    </Stack>
                )}

                {/* SENT ACTIONS */}
                {variant === "sent" && normalizedStatus === "PENDING" && (
                    <Button
                    bg="#4A2C2A"
                    color="white"
                    _hover={{ bg: "#6B3D3A" }}
                    _active={{ bg: "#3a2220" }}
                    size="md"
                    borderRadius="full"
                    px={5}
                    w={{ base: "100%", md: "auto" }}
                    onClick={onCancel ?? (() => {})}
                    >
                    Cancel Request
                    </Button>
                )}

                {/* DECLINED — cooldown countdown or visit profile */}
                {variant === "sent" && normalizedStatus === "DECLINED" && (
                    inCooldown ? (
                        <Text fontSize="xs" color="red.500" textAlign={{ base: "left", md: "right" }}>
                            {cooldownLabel}
                        </Text>
                    ) : (
                        <Button
                            bg="#4A2C2A"
                            color="white"
                            _hover={{ bg: "#6B3D3A" }}
                            _active={{ bg: "#3a2220" }}
                            size="md"
                            borderRadius="full"
                            px={5}
                            w={{ base: "100%", md: "auto" }}
                            onClick={onSendInterest ?? (() => {})}
                        >
                            Visit Profile
                        </Button>
                    )
                )}

                {/* CONNECTION ACTIONS */}
                {variant === "connection" && (
                    <Stack spacing={2} w="100%">
                    {phone && (
                        <Button
                        leftIcon={<PhoneIcon />}
                        size="md"
                        h="44px"
                        w="100%"
                        onClick={onViewContact}
                        >
                        {phone}
                        </Button>
                    )}
                    {email && (
                        <Button leftIcon={<EmailIcon />} size="md" h="44px" w="100%">
                        {email}
                        </Button>
                    )}
                    </Stack>
                )}
            </VStack>
        </Stack>
        </Box>
    );
    };
